/**
 * CR3 — ownerq. Owner verification round (round 2), answered in-app within the
 * AppShell. Owner only. The Owner reviews AI-aggregated PIC findings and
 * confirms/adds their perspective.
 *
 * Same question renderers as the PIC form (QuestionField), debounced autosave
 * (PATCH …/owner/answers, "Tersimpan otomatis"), and submit (POST …/owner/submit)
 * with required validation. A progress side panel + privacy note match the POC.
 *
 * States: loading / not-ready (round not generated yet) / error / ready /
 * in-flight / submitted (completed).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Send,
  Loader2,
  Clock,
  Cloud,
  CloudOff,
  ArrowRight,
} from 'lucide-react';
import type { AnswerInput, QuestionDTO } from '@klinik/shared';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Toast } from '../components/ui/Toast';
import {
  QuestionField,
  normalizeAnswerValue,
  isAnswered,
  type AnswerValue,
} from '../components/QuestionField';
import { useAuth } from '../lib/auth';
import { fadeOnly, fadeRise, staggerContainer } from '../lib/motion';
import { useToast } from '../lib/useToast';
import {
  getOwnerQuestionnaire,
  saveOwnerAnswers,
  submitOwnerAnswers,
  useAutosave,
  friendlyError,
  type OwnerQuestionnaireData,
} from '../lib/questionnaires';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'not_ready'; message: string }
  | { phase: 'error'; message: string }
  | { phase: 'submitted' }
  | { phase: 'ready'; data: OwnerQuestionnaireData };

export function OwnerVerification() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const variants = reduce ? fadeOnly : fadeRise;
  const companyId = user?.companyId ?? null;
  const { toast, show, dismiss } = useToast();

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingIds, setMissingIds] = useState<Set<string>>(new Set());

  const { status: saveStatus, schedule, flush } = useAutosave((a) =>
    companyId ? saveOwnerAnswers(companyId, a) : Promise.resolve({ ok: false as const, error: { code: 'not_found', message: '' } }),
  );

  useEffect(() => {
    if (!companyId) {
      setState({ phase: 'error', message: 'Halaman ini untuk akun Owner perusahaan.' });
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await getOwnerQuestionnaire(companyId);
      if (cancelled) return;
      if (!res.ok) {
        if (res.error.code === 'not_found') {
          setState({
            phase: 'not_ready',
            message:
              'Kuesioner verifikasi Owner belum siap. Tim Nine Co menyiapkannya setelah seluruh jawaban PIC terkumpul & diolah AI.',
          });
        } else {
          setState({ phase: 'error', message: friendlyError(res.error.code, res.error.message) });
        }
        return;
      }
      if (res.data.questionnaire.status === 'completed') {
        setState({ phase: 'submitted' });
        return;
      }
      const initial: Record<string, AnswerValue> = {};
      const byQ = new Map(res.data.savedAnswers.map((a) => [a.questionId, a.value]));
      for (const q of res.data.questions) {
        initial[q.id] = normalizeAnswerValue(q.type, byQ.get(q.id));
      }
      setAnswers(initial);
      setState({ phase: 'ready', data: res.data });
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const questions = state.phase === 'ready' ? state.data.questions : [];
  const answeredCount = useMemo(
    () => questions.filter((q) => isAnswered(q.type, answers[q.id] ?? null)).length,
    [questions, answers],
  );
  const total = questions.length;
  const progressPct = total === 0 ? 0 : Math.round((answeredCount / total) * 100);

  function buildPayload(map: Record<string, AnswerValue>): AnswerInput[] {
    return questions.map((q) => ({ questionId: q.id, value: map[q.id] ?? null }));
  }

  function onChange(q: QuestionDTO, value: AnswerValue) {
    setAnswers((prev) => {
      const next = { ...prev, [q.id]: value };
      schedule(buildPayload(next));
      return next;
    });
    if (missingIds.has(q.id) && isAnswered(q.type, value)) {
      setMissingIds((prev) => {
        const n = new Set(prev);
        n.delete(q.id);
        return n;
      });
    }
  }

  async function onSubmit() {
    if (submitting || !companyId) return;
    setSubmitError(null);
    const missing = new Set(
      questions.filter((q) => q.required && !isAnswered(q.type, answers[q.id] ?? null)).map((q) => q.id),
    );
    if (missing.size > 0) {
      setMissingIds(missing);
      setSubmitError(`Masih ada ${missing.size} pertanyaan wajib yang belum dijawab.`);
      const first = questions.find((q) => missing.has(q.id));
      if (first) document.getElementById(`oq-${first.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    await flush();
    const res = await submitOwnerAnswers(companyId, buildPayload(answers));
    setSubmitting(false);
    if (res.ok) {
      show('success', 'Verifikasi Owner terkirim ke Nine Co.');
      setState({ phase: 'submitted' });
    } else if (res.error.code === 'already_submitted') {
      setState({ phase: 'submitted' });
    } else {
      setSubmitError(friendlyError(res.error.code, res.error.message));
    }
  }

  return (
    <AppShell active="gcu">
      <Toast toast={toast} onDismiss={dismiss} />
      <motion.div initial="hidden" animate="show" variants={staggerContainer}>
        <motion.div variants={variants} className="mb-4 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-bold text-slate-900">
            Kuesioner Verifikasi Owner
          </h1>
          <Badge tone="progress">Ronde 2 — Owner</Badge>
        </motion.div>

        {state.phase === 'loading' ? (
          <div className="h-64">
            <Spinner label="Memuat kuesioner verifikasi…" />
          </div>
        ) : state.phase === 'not_ready' ? (
          <Card>
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-amber-50 text-amber-500">
                <Clock size={26} aria-hidden />
              </span>
              <div>
                <div className="font-display text-base font-bold text-slate-800">
                  Verifikasi belum tersedia
                </div>
                <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{state.message}</p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
              >
                Kembali ke Dashboard <ArrowRight size={14} aria-hidden />
              </button>
            </div>
          </Card>
        ) : state.phase === 'error' ? (
          <Card>
            <div className="flex items-start gap-3 text-sm text-red-700">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
              <div className="flex-1">
                <div className="font-semibold">Gagal memuat kuesioner</div>
                <div className="mt-0.5">{state.message}</div>
              </div>
            </div>
          </Card>
        ) : state.phase === 'submitted' ? (
          <SubmittedState reduce={reduce} onHome={() => navigate('/')} />
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Form */}
            <motion.div variants={variants} className="lg:col-span-2">
              <Card>
                <div className="mb-1 font-display text-lg font-bold text-slate-900">
                  Verifikasi temuan dari para PIC
                </div>
                <p className="mb-4 text-sm text-slate-500">
                  Jawaban PIC sudah diolah AI. Mohon konfirmasi atau kemukakan pandangan Anda sebagai
                  Owner. Jawaban tersimpan otomatis.
                </p>

                <div className="space-y-3">
                  {questions.map((q, i) => (
                    <div key={q.id} id={`oq-${q.id}`}>
                      <div
                        className={
                          missingIds.has(q.id) ? 'rounded-xl ring-2 ring-red-300 ring-offset-2' : ''
                        }
                      >
                        <QuestionField
                          question={q}
                          index={i + 1}
                          value={answers[q.id] ?? null}
                          onChange={(v) => onChange(q, v)}
                          disabled={submitting}
                        />
                      </div>
                      {missingIds.has(q.id) && (
                        <div className="mt-1 pl-1 text-xs font-medium text-red-500">
                          Pertanyaan ini wajib dijawab.
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {submitError && (
                  <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
                    <span>{submitError}</span>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                  <SaveIndicator status={saveStatus} />
                  <button
                    onClick={() => void onSubmit()}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" aria-hidden />
                    ) : (
                      <Send size={16} aria-hidden />
                    )}
                    {submitting ? 'Mengirim…' : 'Kirim ke Nine Co'}
                  </button>
                </div>
              </Card>
            </motion.div>

            {/* Progress + privacy */}
            <motion.div variants={variants} className="space-y-4">
              <Card>
                <div className="mb-2 text-sm font-semibold text-slate-800">Progres</div>
                <div className="mb-2 flex items-end gap-2">
                  <div className="font-display text-2xl font-bold text-slate-900">
                    {answeredCount} / {total}
                  </div>
                  <span className="mb-1 text-xs text-slate-400">terjawab</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <motion.div
                    className="h-1.5 rounded-full bg-brand"
                    animate={{ width: `${progressPct}%` }}
                    transition={reduce ? { duration: 0 } : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  {total} pertanyaan verifikasi. Anda bisa melanjutkan kapan saja — jawaban tersimpan
                  otomatis.
                </div>
              </Card>
              <Card>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <ShieldCheck size={16} aria-hidden /> Privasi
                </div>
                <div className="text-xs text-slate-500">
                  Jawaban Anda terenkripsi &amp; hanya dilihat tim Nine Co untuk menyusun Laporan
                  GCU.
                </div>
              </Card>
            </motion.div>
          </div>
        )}
      </motion.div>
    </AppShell>
  );
}

function SaveIndicator({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
        <Loader2 size={13} className="animate-spin" aria-hidden /> Menyimpan…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-500" role="status">
        <CloudOff size={13} aria-hidden /> Gagal menyimpan
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-400" role="status">
      <Cloud size={13} aria-hidden /> {status === 'saved' ? 'Tersimpan otomatis' : 'Tersimpan otomatis'}
    </span>
  );
}

function SubmittedState({ reduce, onHome }: { reduce: boolean | null; onHome: () => void }) {
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    >
      <Card>
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-green-50 text-green-600">
            <CheckCircle2 size={34} aria-hidden />
          </span>
          <div>
            <div className="font-display text-xl font-bold text-slate-900">Verifikasi terkirim</div>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              Terima kasih. Jawaban verifikasi Anda sudah diterima Nine Co. Tim akan menyusun Laporan
              General Check-Up dan memberi tahu Anda saat siap.
            </p>
          </div>
          <button
            onClick={onHome}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
          >
            Kembali ke Dashboard <ArrowRight size={14} aria-hidden />
          </button>
        </div>
      </Card>
    </motion.div>
  );
}
